import styles from "../../../scss/Form.module.scss";
import useFormErrorHandling from "hooks/useFormErrorHandling";
import { Field } from "components/Shared";
import { SaveIcon } from "components/Layout/MainLayout/Sidebar/icons/SaveIcon";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Box, Card, CardContent, Button, Typography, Grid } from "@mui/material";
import { FIELDS, ICompanyForm, ICompanyFormProps, initialValues, companyFormSchema } from "./Form.config";
import { CompanyStatus } from "components/Shared/CompanyStatus";
import { useIntl } from "react-intl";
import { useGenerateApiKeyMutation, useRevokeApiKeyMutation } from "redux/api/companyApi";
import { useEffect, useState } from "react";
import { useAppSelector } from "redux/store";
import { IUserLoggedState } from "redux/features/loggedUserSlice";
import { UserRoles } from "Enums/UserRoles";
import { toast } from "react-toastify";


// Image field height

const IMAGE_HEIGHT = "9rem";

/**
 * Component to render news form
 */

export const Form = (props: ICompanyFormProps) => {
  const { formatMessage } = useIntl();

  const { currentCompany, onSubmit, apiError, provinces } = props;
  const { user:loggedUser } = useAppSelector(
        (state: { userLoggedState: IUserLoggedState }) => state.userLoggedState
      );

  const [requestApiKey, { isSuccess: isGenerateApiKeySuccess, isError: isGenerateApiKeyError, data: apiKeyData }] = useGenerateApiKeyMutation();
  const [revokeApiKey, { isLoading, isSuccess, isError }] = useRevokeApiKeyMutation();
  
  // Form configuration


  const initial = initialValues(currentCompany, props.companyKey ?? "");
  const methods = useForm<ICompanyForm>({
    resolver: zodResolver(companyFormSchema),
    defaultValues: {
      ...initial,
      verificationList: Array.isArray(initial.verificationList) && initial.verificationList.length
        ? initial.verificationList
        : ["NEOCHECK"],
    }
  });

  useEffect(() => {
    if (apiKeyData) {
      methods.setValue('key', apiKeyData.token); // Assume that the response has a field 'apiKey'
    }
  }, [apiKeyData]);
  // Submit handler function

  const onSubmitHandler: SubmitHandler<ICompanyForm> = (values: ICompanyForm) => {

    const formData = new FormData();

    if (values.image) formData.append("image", values.image as Blob);

    const { key, ...formDataValues } = values;

    formData.append("data", JSON.stringify(formDataValues));

    onSubmit(formData);
  };

  const onRequestApiKeyHandler = () => {
    if (currentCompany?.nif) {
      requestApiKey({ companyId: currentCompany.nif });
    }    
  }
  // Error handler hook

  useFormErrorHandling(methods.setError, apiError);

  useEffect(() => {
    if (props.companyKey) {
      methods.setValue('key', props.companyKey);
    }
  }, [props.companyKey]);

  const handleRevoke = async () => {
    try {
      const cifValue = currentCompany?.nif
      const response = await revokeApiKey({ companyId: cifValue ?? '' }).unwrap();
      toast.success(response.message);
      methods.setValue('key', '');

    } catch (error) {
      toast.error("Failed to revoke token");
    }
  };
  
  return (
    <Card>
      <CardContent className={styles.cardContent}>
        <FormProvider {...methods}>
          <Box
            component="form"
            noValidate
            autoComplete="off"
            onSubmit={methods.handleSubmit(onSubmitHandler)}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {currentCompany && (
                  <Box width={"100%"} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <Typography
                      fontWeight={700}
                      fontSize={"16px"}
                      color={"#313131"}
                      sx={{ marginRight: '16px' }}
                    >{currentCompany.name}</Typography>
                    <CompanyStatus company={currentCompany} />
                  </Box>
                )}
              </Grid>
              <Grid item xs={6}

              >
                <Field.Image
                  image={currentCompany?.image}
                  name={FIELDS.IMAGE.FIELD}
                  cssImage={{
                    width: "9rem",
                    height: IMAGE_HEIGHT,
                    objectFit: "cover",
                    objectPosition: "center",
                  }}
                  imageLoading={false}
                  height={IMAGE_HEIGHT}
                />
              </Grid>

              <Grid item xs={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field.TextField
                      name={FIELDS.NIF.FIELD}
                      label={formatMessage({ id: FIELDS.NIF.LABEL })}
                      defaultValue={currentCompany?.nif}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field.TextField
                      name={FIELDS.NAME.FIELD}
                      label={formatMessage({ id: FIELDS.NAME.LABEL })}
                      defaultValue={currentCompany?.name}
                    />
                  </Grid>

                </Grid>
              </Grid>
              {currentCompany && (
                <Grid item xs={6}>
                  <Field.DateTimePicker
                    control={methods.control}
                    name={FIELDS.DESACTIVATED_AT.FIELD}
                    label={formatMessage({ id: FIELDS.DESACTIVATED_AT.LABEL })}
                    defaultValue={currentCompany?.deactivatedAt}
                    helperText={methods.formState.errors[FIELDS.DESACTIVATED_AT.FIELD]?.message || ''}
                    multiple={false}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="h2"
                  sx={
                    {
                      fontWeight: 300,
                      fontSize: "25px",
                    }
                  }
                >
                  {formatMessage({ id: "company.contactLegalTerms" })}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Field.TextField
                  name={FIELDS.EMAIL.FIELD}
                  label={formatMessage({ id: FIELDS.EMAIL.LABEL })}
                  defaultValue={currentCompany?.email}
                />
              </Grid>
              <Grid item xs={6}>
                <Field.TextField
                  name={FIELDS.PHONE_NUMBER.FIELD}
                  label={formatMessage({ id: FIELDS.PHONE_NUMBER.LABEL })}
                  defaultValue={currentCompany?.phoneNumber}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h2"
                  sx={
                    {
                      fontWeight: 300,
                      fontSize: "25px",
                    }
                  }
                >
                  {formatMessage({ id: "address" })}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Field.TextField
                  name={FIELDS.ADDRESS_TYPE.FIELD}
                  label={formatMessage({ id: FIELDS.ADDRESS_TYPE.LABEL })}
                  defaultValue={currentCompany?.addressType}
                />
              </Grid>

              <Grid item xs={8}>
                <Field.TextField
                  name={FIELDS.ADDRESS_NAME.FIELD}
                  label={formatMessage({ id: FIELDS.ADDRESS_NAME.LABEL })}
                  defaultValue={currentCompany?.addressName}
                />
              </Grid>

              <Grid item xs={2}>
                <Field.TextField
                  name={FIELDS.ADDRESS_NUMBER.FIELD}
                  label={formatMessage({ id: FIELDS.ADDRESS_NUMBER.LABEL })}
                  defaultValue={currentCompany?.addressNumber}
                />
              </Grid>

              <Grid item xs={12}>
                <Field.TextField
                  name={FIELDS.ADDRESS_DETAILS.FIELD}
                  label={formatMessage({ id: FIELDS.ADDRESS_DETAILS.LABEL })}
                  defaultValue={currentCompany?.addressDetails}
                />
              </Grid>

              <Grid item xs={4}>
                <Field.TextField
                  name={FIELDS.POSTAL_CODE.FIELD}
                  label={formatMessage({ id: FIELDS.POSTAL_CODE.LABEL })}
                  defaultValue={currentCompany?.postalCode}
                />
              </Grid>

              <Grid item xs={4}>
                <Field.Select
                  control={methods.control}
                  name={FIELDS.PROVINCE.FIELD}
                  label={formatMessage({ id: FIELDS.PROVINCE.LABEL })}
                  defaultValue={provinces.find((province) => province.value === currentCompany?.province)}
                  multiple={false}
                  helperText={""}
                  fieldValue="value"
                  fieldName="label"
                  variant="outlined"
                  options={provinces}
                />
              </Grid>


              <Grid item xs={4}>
                <Field.TextField
                  name={FIELDS.LOCALITY.FIELD}
                  label={formatMessage({ id: FIELDS.LOCALITY.LABEL })}
                  defaultValue={currentCompany?.locality}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h2"
                  sx={
                    {
                      fontWeight: 300,
                      fontSize: "25px",
                    }
                  }
                >
                  {formatMessage({ id: "compliance" })}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Grid item xs={6}>
                    <Field.InputNumber
                      control={methods.control}
                      name={FIELDS.LIMIT.FIELD}
                      label={formatMessage({ id: FIELDS.LIMIT.LABEL })}
                      defaultValue={currentCompany?.limit}
                      helperText={methods.formState.errors[FIELDS.LIMIT.FIELD]?.message || ''}
                      style={{ marginBottom: "0px" }}
                    />
                  </Grid>
                  <Typography
                    fontWeight={500}
                    fontSize={"12px"}
                    color={"#313131"}
                  >
                    {formatMessage({ id: "company.limit.explain" })}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h2"
                  sx={
                    {
                      fontWeight: 300,
                      fontSize: "25px",
                    }
                  }
                >
                  {formatMessage({ id: "company.verificationList" })}
                </Typography>
                <Box>
                  <Field.CheckBoxGroup
                    name={FIELDS.VERIFICATION_LIST.FIELD}
                    options={["NEOCHECK", "VERIFY"]}
                    labelMap={{
                      NEOCHECK: formatMessage({ id: "company.verifiactionList.NEOCHECK" }),
                      VERIFY: formatMessage({ id: "company.verifiactionList.VERIFY" }),
                    }}
                  />
                  <Typography
                    fontWeight={500}
                    fontSize={"12px"}
                    color={"#313131"}
                    sx={{ mt: 1 }}
                  >
                    {formatMessage({ id: "company.verificationList.explain" })}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h2"
                  sx={
                    {
                      fontWeight: 300,
                      fontSize: "25px",
                    }
                  }
                >
                  {formatMessage({ id: "company.prestaShop.title" })}
                </Typography>
                <Box>
                  <Field.CheckBox
                    name={FIELDS.ACTIVE_PRESTASHOP.FIELD}
                    label={formatMessage({ id: FIELDS.ACTIVE_PRESTASHOP.LABEL })}
                    defaultValue={currentCompany?.activePrestaShop?.toString()}
                  />
                  <Typography
                    fontWeight={500}
                    fontSize={"12px"}
                    color={"#313131"}
                    sx={{ mt: 1 }}
                  >
                    {formatMessage({ id: "company.prestaShop.explain" })}
                  </Typography>
                </Box>
              </Grid>
              
              {currentCompany && (
                <>
                <Grid item xs={12}>
                <Typography variant="h2"
                  sx={
                    {
                      fontWeight: 300,
                      fontSize: "25px",
                    }
                  }
                >
                  {formatMessage({ id: "company.key" })}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Grid item xs={6}>
                    <Field.TextField
                      name={FIELDS.KEY.FIELD}
                      label={formatMessage({ id: FIELDS.KEY.LABEL })}
                      defaultValue={currentCompany?.key}
                      helperText={methods.formState.errors[FIELDS.KEY.FIELD]?.message || ''}
                      style={{ marginBottom: "0px" }}
                      disabled
                    />
                  </Grid>
                  <Typography
                    fontWeight={500}
                    fontSize={"12px"}
                    color={"#313131"}
                  >
                    {formatMessage({ id: "company.key.explain" })}
                  </Typography>
                </Box>
              </Grid>
              </>
                )}
              

            </Grid>

            {methods.formState.errors &&
              methods.formState.errors.general?.message && (
                <>{methods.formState.errors.general.message}</>
              )}

            <Box display="flex" justifyContent="flex-end" sx={{ mt: 3, gap: 2 }}>
              {loggedUser?.role===UserRoles.SUPERADMIN && currentCompany && (
              <>
                <Button variant="outlined" color="secondary" type="button" 
                    onClick={() => {
                      onRequestApiKeyHandler();
                    }} startIcon={<SaveIcon />}>
                  <Typography
                    fontWeight={500}
                    fontSize={"16px"}
                    color="#000000"
                    alignContent="center"
                  >
                    {formatMessage({ id: "company.generate.key" })}
                  </Typography>
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleRevoke}
                  disabled={!methods.watch('key')  || isLoading}
                  startIcon={<SaveIcon />}
                >
                  {formatMessage({ id: "company.revoke.key" })}
                </Button>
              </>          
                            )}
              <Button variant="contained" color="primary" type="submit" startIcon={<SaveIcon />}>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  color="#FFF"
                  alignContent="center"
                >
                  {formatMessage({ id: "save" })}
                </Typography>
              </Button>
            </Box>
          </Box>
        </FormProvider>
      </CardContent>
    </Card>
  );
};